import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import toast from 'react-hot-toast';
import { 
  Eye, EyeOff, Sun, Moon, ArrowRight, Shield, Smartphone, 
  BarChart3, ClipboardList, QrCode, Settings, FileCheck, 
  Mail, Lock, CheckCircle2, Sparkles, Activity, Check,
  Zap, Wrench, UserCheck, ShieldCheck, ChevronRight
} from 'lucide-react';
import shortLogo from '../img/shortlogo.png';
import fullLogo from '../img/fulllogo.png';

const DEMO_ROLES = [
  {
    id: 'super_admin',
    label: 'Super Admin',
    email: 'admin@fieldops.com',
    password: 'Admin@123',
    icon: ShieldCheck,
    tag: 'Full Access',
    color: '#0EA5E9'
  },
  {
    id: 'admin',
    label: 'Ops Manager',
    email: 'manager@fieldops.com',
    password: 'Admin@123',
    icon: UserCheck,
    tag: 'Management',
    color: '#8B5CF6'
  },
  {
    id: 'technician',
    label: 'Field Tech',
    email: 'tech1@fieldops.com',
    password: 'Admin@123',
    icon: Wrench,
    tag: 'Mobile Execution',
    color: '#10B981'
  }
];

const PLATFORM_METRICS = [
  { value: '99.9%', label: 'SLA Compliance', color: '#10B981' },
  { value: '15.4k+', label: 'Jobs Completed', color: '#0EA5E9' },
  { value: '<12m', label: 'First Response', color: '#8B5CF6' },
];

export default function LoginPage() {
  const { login } = useAuth();
  const { isDark, toggle } = useTheme();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: 'admin@fieldops.com', password: 'Admin@123' });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeRole, setActiveRole] = useState('super_admin');
  const [rememberMe, setRememberMe] = useState(true);

  const selectDemoRole = (role) => {
    setActiveRole(role.id);
    setForm({ email: role.email, password: role.password });
    toast.success(`Loaded demo credentials for ${role.label}`, {
      icon: '✨',
      style: { borderRadius: '12px', background: '#0F172A', color: '#fff', fontSize: '13px' }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form.email, form.password);
      toast.success('Authentication successful! Loading dashboard...', {
        icon: '🚀',
        duration: 2500
      });
      navigate('/dashboard');
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Login connection failed';
      console.error('Login Error Detail:', err);
      toast.error(`${msg}`, { duration: 5000 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-viewport">
      <style>{`
        @keyframes meshDrift {
          0% { transform: scale(1) translate(0, 0) rotate(0deg); }
          50% { transform: scale(1.15) translate(-20px, 25px) rotate(4deg); }
          100% { transform: scale(1) translate(0, 0) rotate(0deg); }
        }
        @keyframes beaconPulse {
          0% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.6); }
          70% { box-shadow: 0 0 0 10px rgba(16, 185, 129, 0); }
          100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
        }
        @keyframes subtleFloat {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        @keyframes shimmerLine {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }
        @keyframes buttonShimmer {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        .login-viewport {
          min-height: 100vh;
          display: flex;
          background: ${isDark ? '#060B14' : '#F1F5F9'};
          color: ${isDark ? '#F8FAFC' : '#0F172A'};
          font-family: var(--font-body, 'DM Sans', -apple-system, sans-serif);
          position: relative;
          overflow: hidden;
        }

        /* Hero Left Section */
        .hero-panel {
          width: 50%;
          background: linear-gradient(165deg, #09101F 0%, #050B16 100%);
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          padding: 56px 64px;
          position: relative;
          overflow: hidden;
          border-right: 1px solid rgba(255, 255, 255, 0.07);
        }
        .hero-glow-1 {
          position: absolute;
          top: -10%;
          left: -10%;
          width: 600px;
          height: 600px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(14, 165, 233, 0.22) 0%, transparent 70%);
          filter: blur(80px);
          animation: meshDrift 18s ease-in-out infinite;
          pointer-events: none;
        }
        .hero-glow-2 {
          position: absolute;
          bottom: -15%;
          right: -10%;
          width: 550px;
          height: 550px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(139, 92, 246, 0.18) 0%, transparent 70%);
          filter: blur(80px);
          animation: meshDrift 22s ease-in-out infinite reverse;
          pointer-events: none;
        }
        .grid-overlay {
          position: absolute;
          inset: 0;
          background-image: radial-gradient(rgba(255, 255, 255, 0.08) 1px, transparent 1px);
          background-size: 32px 32px;
          opacity: 0.6;
          pointer-events: none;
        }

        /* Telemetry Card */
        .telemetry-card {
          background: rgba(15, 23, 42, 0.65);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 20px;
          padding: 24px;
          box-shadow: 0 20px 40px -10px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.1);
          position: relative;
          overflow: hidden;
          margin-top: 36px;
        }
        .telemetry-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 2px;
          background: linear-gradient(90deg, transparent, #0EA5E9, #8B5CF6, transparent);
        }

        /* Form Right Section */
        .portal-panel {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 48px 32px;
          position: relative;
          background: ${isDark ? '#060B14' : '#F8FAFC'};
          overflow-y: auto;
        }

        .portal-bg-pattern {
          position: absolute;
          inset: 0;
          background: ${isDark 
            ? 'radial-gradient(circle at 80% 20%, rgba(14, 165, 233, 0.08) 0%, transparent 50%), radial-gradient(circle at 20% 80%, rgba(139, 92, 246, 0.06) 0%, transparent 50%)'
            : 'radial-gradient(circle at 80% 20%, rgba(14, 165, 233, 0.06) 0%, transparent 50%), radial-gradient(circle at 20% 80%, rgba(139, 92, 246, 0.04) 0%, transparent 50%)'};
          pointer-events: none;
        }

        .login-card-wrapper {
          width: 100%;
          max-width: 440px;
          background: ${isDark ? 'rgba(15, 23, 42, 0.75)' : '#FFFFFF'};
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border: 1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(226, 232, 240, 0.9)'};
          border-radius: 24px;
          padding: 38px 34px;
          box-shadow: ${isDark 
            ? '0 25px 50px -12px rgba(0, 0, 0, 0.6), 0 0 35px -5px rgba(14, 165, 233, 0.12)' 
            : '0 20px 45px -10px rgba(14, 165, 233, 0.1), 0 4px 15px rgba(0, 0, 0, 0.04)'};
          position: relative;
          z-index: 2;
          transition: all 0.3s ease;
        }

        /* Role Selector Pills */
        .role-pill {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          padding: 10px 6px;
          border-radius: 12px;
          cursor: pointer;
          background: ${isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(241, 245, 249, 0.8)'};
          border: 1px solid ${isDark ? 'rgba(255, 255, 255, 0.07)' : 'rgba(226, 232, 240, 0.8)'};
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .role-pill:hover {
          background: ${isDark ? 'rgba(255, 255, 255, 0.07)' : '#FFFFFF'};
          border-color: rgba(14, 165, 233, 0.4);
          transform: translateY(-1px);
        }
        .role-pill.active {
          background: ${isDark ? 'rgba(14, 165, 233, 0.12)' : 'rgba(14, 165, 233, 0.08)'};
          border-color: #0EA5E9;
          box-shadow: 0 0 14px rgba(14, 165, 233, 0.25);
        }

        /* Inputs with Animated Focus Ring */
        .modern-input-group {
          position: relative;
          display: flex;
          align-items: center;
        }
        .modern-input-group svg.input-icon {
          position: absolute;
          left: 14px;
          color: ${isDark ? '#64748B' : '#94A3B8'};
          pointer-events: none;
          transition: color 0.2s ease;
        }
        .modern-input-group:focus-within svg.input-icon {
          color: #0EA5E9;
        }
        .modern-input {
          width: 100%;
          height: 48px;
          padding: 0 44px 0 42px;
          background: ${isDark ? 'rgba(2, 6, 23, 0.5)' : '#F8FAFC'};
          border: 1.5px solid ${isDark ? 'rgba(255, 255, 255, 0.09)' : '#E2E8F0'};
          border-radius: 12px;
          color: ${isDark ? '#FFFFFF' : '#0F172A'};
          font-size: 14px;
          font-weight: 500;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          outline: none;
        }
        .modern-input:focus {
          border-color: #0EA5E9;
          background: ${isDark ? 'rgba(2, 6, 23, 0.8)' : '#FFFFFF'};
          box-shadow: 0 0 0 4px rgba(14, 165, 233, 0.15);
        }
        .modern-input::placeholder {
          color: ${isDark ? '#475569' : '#94A3B8'};
        }

        /* Gradient Glow Submit Button */
        .btn-portal-submit {
          width: 100%;
          height: 50px;
          border-radius: 12px;
          border: none;
          background: linear-gradient(135deg, #0EA5E9 0%, #2563EB 50%, #4F46E5 100%);
          background-size: 200% 200%;
          color: #FFFFFF;
          font-size: 15px;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 10px 25px -5px rgba(14, 165, 233, 0.45);
          position: relative;
          overflow: hidden;
        }
        .btn-portal-submit:hover:not(:disabled) {
          transform: translateY(-1.5px);
          box-shadow: 0 15px 30px -5px rgba(14, 165, 233, 0.6);
          background-position: 100% 50%;
        }
        .btn-portal-submit:active:not(:disabled) {
          transform: translateY(0px);
        }
        .btn-portal-submit:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        /* Responsive Breakpoints */
        @media (max-width: 980px) {
          .hero-panel { display: none !important; }
          .portal-panel {
            padding: 32px 20px;
          }
          .mobile-brand-header {
            display: flex !important;
          }
        }
      `}</style>

      {/* ── LEFT SHOWCASE PANEL ──────────────────────────────── */}
      <div className="hero-panel">
        <div className="hero-glow-1" />
        <div className="hero-glow-2" />
        <div className="grid-overlay" />

        {/* Top Brand Tag */}
        <div style={{ position: 'relative', zIndex: 2 }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '10px 20px',
            borderRadius: 16,
            background: 'rgba(255, 255, 255, 0.96)',
            boxShadow: '0 12px 30px rgba(0, 0, 0, 0.25)',
          }}>
            <img src={fullLogo} alt="FieldOps Pro - Powered by PXTech" style={{ height: 42, objectFit: 'contain' }} />
          </div>
        </div>

        {/* Center Main Value Proposition */}
        <div style={{ position: 'relative', zIndex: 2, margin: '40px 0' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '6px 14px', borderRadius: 99,
            background: 'rgba(14, 165, 233, 0.1)',
            border: '1px solid rgba(14, 165, 233, 0.2)',
            color: '#38BDF8', fontSize: 12, fontWeight: 600,
            marginBottom: 20
          }}>
            <Sparkles size={14} /> Intelligent Autonomous Operations
          </div>

          <h1 style={{
            fontSize: 42, fontWeight: 800, color: '#FFFFFF',
            lineHeight: 1.15, letterSpacing: -1.2, marginBottom: 18
          }}>
            Master Every Asset.<br />
            <span style={{
              background: 'linear-gradient(135deg, #38BDF8 0%, #818CF8 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              Orchestrate Every Job.
            </span>
          </h1>

          <p style={{ color: '#94A3B8', fontSize: 15, lineHeight: 1.6, maxWidth: 480 }}>
            Bridge your central dispatch control room with technicians on the frontline. 
            From scan-to-work QR verification to GPS evidence capture and automated audit PDF sign-offs.
          </p>

          {/* Live Telemetry Radar Card */}
          <div className="telemetry-card">
            {/* Telemetry Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{
                  width: 8, height: 8, borderRadius: '50%',
                  background: '#10B981',
                  animation: 'beaconPulse 2s infinite'
                }} />
                <span style={{ fontSize: 11, fontWeight: 700, color: '#10B981', letterSpacing: 0.8, textTransform: 'uppercase' }}>
                  Live Ops Stream
                </span>
              </div>
              <span style={{ fontSize: 11, color: '#64748B', fontFamily: 'var(--font-mono, monospace)' }}>
                PORT: 5045 • SYNC OK
              </span>
            </div>

            {/* Active Ticket Simulation */}
            <div style={{
              background: 'rgba(2, 6, 23, 0.6)',
              borderRadius: 12, padding: '14px 16px',
              border: '1px solid rgba(255, 255, 255, 0.06)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div>
                  <div style={{ color: '#F1F5F9', fontSize: 13, fontWeight: 700 }}>Chiller Unit Alpha • Compressor Room B</div>
                  <div style={{ color: '#64748B', fontSize: 11, marginTop: 2 }}>JOB-2026-089 • Preventive Maintenance</div>
                </div>
                <span style={{
                  fontSize: 10, fontWeight: 700,
                  color: '#38BDF8', background: 'rgba(14, 165, 233, 0.15)',
                  padding: '3px 8px', borderRadius: 6
                }}>IN PROGRESS</span>
              </div>

              {/* Progress bar */}
              <div style={{ height: 6, width: '100%', background: 'rgba(255, 255, 255, 0.08)', borderRadius: 99, overflow: 'hidden', margin: '10px 0 8px 0' }}>
                <div style={{
                  height: '100%', width: '78%',
                  background: 'linear-gradient(90deg, #0EA5E9, #10B981)',
                  borderRadius: 99
                }} />
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, color: '#94A3B8' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <CheckCircle2 size={13} color="#10B981" /> James Chen (Tech Lead)
                </span>
                <span style={{ color: '#38BDF8', fontWeight: 600 }}>78% Completed</span>
              </div>
            </div>

            {/* Metrics Triad */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginTop: 16, paddingTop: 14, borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}>
              {PLATFORM_METRICS.map(m => (
                <div key={m.label} style={{ textAlign: 'center' }}>
                  <div style={{ color: m.color, fontSize: 18, fontWeight: 800, letterSpacing: -0.5 }}>{m.value}</div>
                  <div style={{ color: '#64748B', fontSize: 10, fontWeight: 600, marginTop: 2, textTransform: 'uppercase' }}>{m.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer Security Badges */}
        <div style={{ position: 'relative', zIndex: 2, display: 'flex', alignItems: 'center', gap: 20, color: '#475569', fontSize: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Shield size={14} color="#0EA5E9" /> 256-Bit TLS Security
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Zap size={14} color="#10B981" /> Sub-50ms Edge Telemetry
          </div>
        </div>
      </div>

      {/* ── RIGHT AUTHENTICATION PORTAL ───────────────────────── */}
      <div className="portal-panel">
        <div className="portal-bg-pattern" />

        {/* Theme Toggle in Top Right */}
        <button 
          onClick={toggle} 
          title="Toggle Dark/Light Mode"
          style={{
            position: 'absolute', top: 28, right: 28,
            width: 42, height: 42, borderRadius: 12,
            background: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
            border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
            color: isDark ? '#F1F5F9' : '#0F172A',
            zIndex: 10,
            transition: 'all 0.2s ease'
          }}
        >
          {isDark ? <Sun size={18} /> : <Moon size={18} />}
        </button>

        {/* Mobile Header (Only visible on small screens) */}
        <div className="mobile-brand-header" style={{ display: 'none', flexDirection: 'column', alignItems: 'center', marginBottom: 20, textAlign: 'center' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '8px 16px',
            borderRadius: 14,
            background: 'rgba(255, 255, 255, 0.95)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
            marginBottom: 8
          }}>
            <img src={fullLogo} alt="FieldOps Pro" style={{ height: 38, objectFit: 'contain' }} />
          </div>
          <p style={{ color: '#64748B', fontSize: 13, margin: 0 }}>Operations Control Portal</p>
        </div>

        {/* Login Card */}
        <div className="login-card-wrapper">
          {/* Card Title & Icon */}
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div style={{
              width: 56, height: 56, margin: '0 auto 14px',
              borderRadius: 18,
              background: isDark ? 'rgba(14, 165, 233, 0.12)' : 'rgba(14, 165, 233, 0.08)',
              border: '1px solid rgba(14, 165, 233, 0.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 8px 18px rgba(14, 165, 233, 0.15)'
            }}>
              <img src={shortLogo} alt="Logo" style={{ width: 34, height: 34, objectFit: 'contain' }} />
            </div>
            <h2 style={{
              fontSize: 22, fontWeight: 800,
              color: isDark ? '#FFFFFF' : '#0F172A',
              letterSpacing: -0.5, marginBottom: 6
            }}>
              Welcome Back
            </h2>
            <p style={{
              fontSize: 13, color: isDark ? '#94A3B8' : '#64748B',
              margin: 0
            }}>
              Select your role or enter credentials to continue
            </p>
          </div>

          {/* Quick Demo Role Selector Pills */}
          <div style={{ marginBottom: 24 }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginBottom: 8, fontSize: 11, fontWeight: 700,
              color: isDark ? '#64748B' : '#94A3B8',
              letterSpacing: 0.6, textTransform: 'uppercase'
            }}>
              <span>1-Click Demo Profiles</span>
              <span style={{ color: '#0EA5E9', fontSize: 10 }}>Auto-fill</span>
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              {DEMO_ROLES.map(role => {
                const Icon = role.icon;
                const isSelected = activeRole === role.id;
                return (
                  <button
                    key={role.id}
                    type="button"
                    className={`role-pill ${isSelected ? 'active' : ''}`}
                    onClick={() => selectDemoRole(role)}
                  >
                    <div style={{
                      width: 26, height: 26, borderRadius: 8,
                      background: isSelected ? 'rgba(14, 165, 233, 0.2)' : (isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0,0,0,0.04)'),
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      <Icon size={14} color={isSelected ? '#0EA5E9' : (isDark ? '#94A3B8' : '#64748B')} />
                    </div>
                    <span style={{
                      fontSize: 12, fontWeight: isSelected ? 700 : 600,
                      color: isSelected ? (isDark ? '#FFFFFF' : '#0284C7') : (isDark ? '#CBD5E1' : '#475569')
                    }}>
                      {role.label}
                    </span>
                    <span style={{
                      fontSize: 9, fontWeight: 600,
                      color: isSelected ? '#0EA5E9' : '#64748B'
                    }}>
                      {role.tag}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Sign In Form */}
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Email Field */}
            <div>
              <label style={{
                display: 'block', fontSize: 12, fontWeight: 600,
                color: isDark ? '#E2E8F0' : '#334155',
                marginBottom: 6
              }}>
                Work Email
              </label>
              <div className="modern-input-group">
                <Mail className="input-icon" size={17} />
                <input
                  className="modern-input"
                  type="email"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="admin@fieldops.com"
                  required
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <label style={{
                  fontSize: 12, fontWeight: 600,
                  color: isDark ? '#E2E8F0' : '#334155'
                }}>
                  Password
                </label>
                <Link 
                  to="/forgot-password" 
                  style={{
                    fontSize: 12, color: '#0EA5E9',
                    textDecoration: 'none', fontWeight: 600,
                    transition: 'color 0.2s ease'
                  }}
                >
                  Forgot?
                </Link>
              </div>
              <div className="modern-input-group">
                <Lock className="input-icon" size={17} />
                <input
                  className="modern-input"
                  type={showPw ? 'text' : 'password'}
                  value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  style={{
                    position: 'absolute', right: 12,
                    background: 'none', border: 'none',
                    cursor: 'pointer', color: isDark ? '#64748B' : '#94A3B8',
                    padding: 4, display: 'flex', alignItems: 'center'
                  }}
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Remember Me Checkbox */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '2px 0' }}>
              <input
                type="checkbox"
                id="remember"
                checked={rememberMe}
                onChange={e => setRememberMe(e.target.checked)}
                style={{
                  width: 16, height: 16, borderRadius: 4,
                  accentColor: '#0EA5E9', cursor: 'pointer'
                }}
              />
              <label 
                htmlFor="remember" 
                style={{ fontSize: 13, color: isDark ? '#94A3B8' : '#64748B', cursor: 'pointer', userSelect: 'none' }}
              >
                Remember this workstation for 30 days
              </label>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="btn-portal-submit"
              disabled={loading}
            >
              {loading ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 18, height: 18, border: '2px solid rgba(255, 255, 255, 0.3)',
                    borderTopColor: '#FFFFFF', borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite'
                  }} />
                  <span>Authenticating...</span>
                </div>
              ) : (
                <>
                  <span>Sign In to Platform</span>
                  <ArrowRight size={17} />
                </>
              )}
            </button>
          </form>

          {/* Workflow Sequence Strip */}
          <div style={{
            marginTop: 28, paddingTop: 20,
            borderTop: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.07)' : 'rgba(226, 232, 240, 0.8)'}`
          }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              position: 'relative', padding: '0 4px'
            }}>
              {[
                { icon: ClipboardList, label: 'Dispatch', color: '#0EA5E9' },
                { icon: QrCode, label: 'QR Scan', color: '#8B5CF6' },
                { icon: Settings, label: 'Perform', color: '#10B981' },
                { icon: FileCheck, label: 'PDF Audit', color: '#F59E0B' }
              ].map(step => {
                const Icon = step.icon;
                return (
                  <div key={step.label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: 10,
                      background: isDark ? 'rgba(2, 6, 23, 0.8)' : 'rgba(241, 245, 249, 0.9)',
                      border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(226, 232, 240, 0.9)'}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      <Icon size={14} color={step.color} />
                    </div>
                    <span style={{ fontSize: 10, fontWeight: 600, color: isDark ? '#64748B' : '#94A3B8' }}>
                      {step.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Legal & Compliance bottom strip */}
        <div style={{
          marginTop: 24, fontSize: 11,
          color: isDark ? '#475569' : '#94A3B8',
          textAlign: 'center'
        }}>
          © 2026 FieldOps Pro • Enterprise Operations Platform
        </div>
      </div>
    </div>
  );
}
